/**
 * The pipeline plan — what actually runs, as independent choices.
 *
 * Flat engine names ("whisperx-gemini-fillerforce") hid two problems: you
 * could not see the flow from the name, and orthogonal choices got welded
 * together, so filler handling existed only on the no-re-cut branch. Here the
 * stages are separate switches, every combination is reachable, and the label
 * on a task states exactly what produced it.
 *
 * The stages, in order:
 *   1. base      — who produces the words and their times
 *   2. geminiCuts— Gemini re-reads each cut and rewrites its label
 *   3. recut     — long clips are broken down again by WhisperX
 *   4. fillers   — how hesitation sounds are kept
 */

export interface EnginePlan {
  /** 'gemini' = Gemini hears + MMS aligner measures. 'whisperx' = one model does both. */
  base: 'gemini' | 'whisperx';
  /** Gemini reads every cut and replaces the label. WhisperX base only. */
  geminiCuts: boolean;
  /**
   * How clips holding several words are broken down (needs geminiCuts):
   *  none   — leave them whole
   *  burst  — cut the span out and let WhisperX transcribe it fresh
   *  guided — keep Gemini's words, ask WhisperX only to place them
   */
  recut: 'none' | 'burst' | 'guided';
  /**
   * Hesitation sounds:
   *  off    — whatever the engine does by default
   *  bias   — WhisperX decodes with a filler-biased prompt
   *  prompt — Gemini is ordered to keep every filler when reading cuts
   *  force  — WhisperX (biased) finds them and Gemini may not touch those clips
   */
  fillers: 'off' | 'bias' | 'prompt' | 'force';
}

export const DEFAULT_PLAN: EnginePlan = {
  base: 'gemini',
  geminiCuts: false,
  recut: 'none',
  fillers: 'off',
};

/** Drop choices that don't apply, so a plan never lies about what will run. */
export function normalizePlan(p: EnginePlan): EnginePlan {
  if (p.base === 'gemini') {
    return { base: 'gemini', geminiCuts: false, recut: 'none', fillers: 'off' };
  }
  const geminiCuts = p.geminiCuts;
  return {
    base: 'whisperx',
    geminiCuts,
    recut: geminiCuts ? p.recut : 'none',
    // 'prompt' and 'force' both act during the Gemini read; without it only
    // WhisperX's own bias is meaningful.
    fillers: geminiCuts ? p.fillers : p.fillers === 'off' ? 'off' : 'bias',
  };
}

/** Compact string for storage: reconstructable, greppable in logs. */
export function planToId(p: EnginePlan): string {
  const n = normalizePlan(p);
  if (n.base === 'gemini') return 'gemini';
  return [
    'whisperx',
    n.geminiCuts ? 'gemini' : null,
    n.recut !== 'none' ? `recut-${n.recut}` : null,
    n.fillers !== 'off' ? `fillers-${n.fillers}` : null,
  ]
    .filter(Boolean)
    .join('+');
}

export function planFromId(id: string | undefined): EnginePlan {
  if (!id) return DEFAULT_PLAN;
  const parts = id.split('+');
  if (parts[0] !== 'whisperx') return DEFAULT_PLAN;
  const recutPart = parts.find((p) => p.startsWith('recut-'));
  const fillerPart = parts.find((p) => p.startsWith('fillers-'));
  return normalizePlan({
    base: 'whisperx',
    geminiCuts: parts.includes('gemini'),
    recut: (recutPart?.slice('recut-'.length) as EnginePlan['recut']) ?? 'none',
    fillers: (fillerPart?.slice('fillers-'.length) as EnginePlan['fillers']) ?? 'off',
  });
}

/** One line naming every stage, for the toggle and the task card. */
export function describePlan(p: EnginePlan): string {
  const n = normalizePlan(p);
  if (n.base === 'gemini') return 'Gemini words → MMS aligner times';
  const steps: string[] = ['WhisperX words+times'];
  if (n.geminiCuts) steps.push('Gemini re-reads each cut');
  if (n.recut === 'burst') steps.push('long clips re-transcribed (burst)');
  if (n.recut === 'guided') steps.push('long clips re-timed (guided)');
  if (n.fillers === 'bias') steps.push('fillers: WhisperX bias');
  if (n.fillers === 'prompt') steps.push('fillers: Gemini ordered to keep');
  if (n.fillers === 'force') steps.push('fillers: WhisperX forced, Gemini locked out');
  return steps.join(' → ');
}
